import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { showToast } from "../../../../../../core/common-service/CommonService";
import { svgs } from "../../../../../../core/constants/svgs";
import useIsMobile from "../../../../../../core/hooks/useMobile";

const InlineChapterEditor = ({
  isVisible,
  chapterToEdit = null,
  onSubmit,
  onCancel,
  general_category_id,
  category_based_type_id,
  category_length_type_id,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [chapterName, setChapterName] = useState("");
  const [questionName, setQuestionName] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [showQuestionField, setShowQuestionField] = useState(false);

  useEffect(() => {
    if (chapterToEdit) {
      // Edit mode - only edit chapter title
      setChapterName(chapterToEdit.name || "");
      setIsEditMode(true);
      setShowQuestionField(false);
    } else {
      // Add mode - chapter title then question
      setChapterName("");
      setQuestionName("");
      setIsEditMode(false);
      setShowQuestionField(false);
    }
  }, [chapterToEdit, isVisible]);

  const handleChapterNameChange = (e) => {
    setChapterName(e.target.value);
  };

  const handleQuestionNameChange = (e) => {
    setQuestionName(e.target.value);
  };

  const handleChapterTitleSubmit = () => {
    if (!chapterName.trim()) {
      showToast({
        type: "error",
        message: t("please_add_chapter_title"),
      });
      return;
    }

    if (isEditMode) {
      // Edit mode - submit immediately with existing questions
      const chapterData = {
        id: chapterToEdit.id,
        general_category_id,
        name: chapterName,
        questions: chapterToEdit.questions,
      };
      onSubmit(chapterData);
    } else {
      // Add mode - show question field
      setShowQuestionField(true);
    }
  };

  const handleQuestionSubmit = () => {
    if (!questionName.trim()) {
      showToast({
        type: "error",
        message: t("please_add_question_title"),
      });
      return;
    }

    // Submit chapter with the single question
    const chapterData = {
      id: Date.now(),
      general_category_id,
      name: chapterName,
      questions: [
        {
          name: questionName,
          question_category_id: Date.now(),
          category_based_type_id,
          category_length_type_id,
        },
      ],
    };
    onSubmit(chapterData);
  };

  const handleKeyPress = (e, isQuestionField = false) => {
    if (e.key === "Enter") {
      if (isQuestionField) {
        handleQuestionSubmit();
      } else {
        handleChapterTitleSubmit();
      }
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="col-xl-12 col-lg mt-1 mt-xl-0"
      style={{
        backgroundColor: "white",
        padding: "15px",
        borderRadius: "30px",
        marginBottom: "1rem",
      }}
    >
      <div className="aai-accordions">
        <div className="accordion accordion-flush">
          <div className="accordion-item">
            {/* Chapter Header */}
            <h2
              className="accordion-header"
              style={{
                paddingBottom: "5px",
              }}
            >
              <div
                className="accordion-button flex-wrap"
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#fdf6f0",
                  border: "1px solid #c47a26",
                  borderRadius: "18px",
                  margin: "0",
                }}
              >
                {!isMobile && (
                  <img
                    src={svgs.draggable}
                    alt="draggable"
                    width={24}
                    style={{ marginRight: "8px" }}
                  />
                )}

                <div
                  className={`${isMobile ? "" : "pe-4"}`}
                  style={{
                    marginRight: isMobile ? "1rem" : "unset",
                    color: "gray",
                    flex: 1,
                  }}
                >
                  {/* Chapter Title Input */}
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "8px" }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("add_your_chapter_title")}
                      value={chapterName}
                      onChange={handleChapterNameChange}
                      onKeyPress={(e) => handleKeyPress(e, false)}
                      style={{
                        border: "1px solid #ffffff",
                        borderRadius: "18px",
                        padding: "6px 10px",
                        fontSize: "inherit",
                        fontFamily: "inherit",
                        flex: 1,
                        outline: "none",
                        boxShadow: "0 0 0 2px rgba(196, 122, 38, 0.1)",
                        backgroundColor: "#fff",
                      }}
                    />
                    <button
                      className="btn btn-sm"
                      onClick={handleChapterTitleSubmit}
                      style={{
                        backgroundColor: "transparent",
                        border: "none",
                        borderRadius: "18px",
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        color: "#c47a26",
                        fontSize: "16px",
                        transition: "color 0.2s ease",
                      }}
                      onMouseOver={(e) => (e.target.style.color = "#a66521")}
                      onMouseOut={(e) => (e.target.style.color = "#c47a26")}
                    >
                      <i className="fa-solid fa-check"></i>
                    </button>
                  </div>
                </div>

                {/* Action buttons */}
                <div
                  className="d-flex"
                  style={{ gap: isMobile ? ".5rem" : "8px" }}
                >
                  {/* Cancel/Delete */}
                  <button
                    className="btn btn-sm"
                    onClick={onCancel}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      borderRadius: "18px",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      color: "#c47a26",
                      fontSize: "14px",
                      transition: "color 0.2s ease",
                    }}
                    onMouseOver={(e) => (e.target.style.color = "#a66521")}
                    onMouseOut={(e) => (e.target.style.color = "#c47a26")}
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            </h2>

            {/* Question Field - Only shows after chapter title is submitted in add mode */}
            {!isEditMode && showQuestionField && (
              <div
                style={{
                  padding: "20px",
                  marginTop: "15px",
                }}
              >
                <div style={{ marginBottom: "10px" }}>
                  {t("enter_question_title")}
                </div>
                <div
                  style={{
                    borderRadius: "18px",
                    padding: "15px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("add_your_question_title")}
                      value={questionName}
                      onChange={handleQuestionNameChange}
                      onKeyPress={(e) => handleKeyPress(e, true)}
                      style={{
                        border: "1px solid #ffffff",
                        borderRadius: "18px",
                        padding: "6px 10px",
                        backgroundColor: "white",
                        outline: "none",
                        boxShadow: "0 0 0 2px rgba(196, 122, 38, 0.1)",
                      }}
                    />
                  </div>

                  <button
                    className="btn btn-sm"
                    onClick={handleQuestionSubmit}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      borderRadius: "6px",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      color: "#c47a26",
                      fontSize: "16px",
                      transition: "color 0.2s ease",
                    }}
                  >
                    <i className="fa-solid fa-check"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineChapterEditor;
